import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Users, Plus, X, CreditCard, Banknote, Receipt, Euro,
  Coffee, UtensilsCrossed, Check, Clock, UserPlus, Trash2,
  Split, Merge, RefreshCw, Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  }).format(value || 0);
};

const statusConfig = {
  free: { label: 'Frei', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', icon: Check },
  occupied: { label: 'Besetzt', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: UtensilsCrossed },
  reserved: { label: 'Reserviert', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Clock },
  billing: { label: 'Abrechnung', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30', icon: Receipt },
};

export default function Tischverwaltung() {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableSession, setTableSession] = useState(null);
  const [products, setProducts] = useState([]);
  
  // Dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showOpenDialog, setShowOpenDialog] = useState(false);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [showSplitDialog, setShowSplitDialog] = useState(false);
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  
  // Form states
  const [newTable, setNewTable] = useState({ table_number: '', capacity: 4, area: '' });
  const [guestCount, setGuestCount] = useState(2);
  const [orderItems, setOrderItems] = useState([]);
  const [payment, setPayment] = useState({ cash: '', card: '', tip: '', receiptType: 'standard' });
  const [setupCount, setSetupCount] = useState(10);
  const [splitItems, setSplitItems] = useState([]);
  const [splitTargetTable, setSplitTargetTable] = useState('');
  const [mergeSourceTables, setMergeSourceTables] = useState([]);

  useEffect(() => {
    loadTables();
    loadProducts();
  }, []);

  const loadTables = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tables/`);
      const data = await response.json();
      setTables(data.tables || []);
    } catch (error) {
      console.error('Error loading tables:', error);
      toast.error('Fehler beim Laden der Tische');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/products/?is_active=true`);
      const data = await response.json();
      setProducts(data || []);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const loadTableSession = async (tableId) => {
    try {
      const response = await fetch(`${API_URL}/api/tables/${tableId}/session`);
      const data = await response.json();
      setTableSession(data);
    } catch (error) {
      console.error('Error loading session:', error);
    }
  };

  const handleTableClick = async (table) => {
    setSelectedTable(table);
    if (table.status === 'occupied') {
      await loadTableSession(table.id);
    } else {
      setTableSession(null);
    }
  };

  const handleOpenTable = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tables/${selectedTable.id}/open?guest_count=${guestCount}`, {
        method: 'POST'
      });
      
      if (!response.ok) throw new Error('Failed to open table');
      
      toast.success(`Tisch ${selectedTable.table_number} geöffnet`, {
        description: `${guestCount} Gäste`
      });
      
      setShowOpenDialog(false);
      loadTables();
      handleTableClick({ ...selectedTable, status: 'occupied' });
    } catch (error) {
      toast.error('Fehler beim Öffnen des Tisches');
    }
  };

  const handleAddOrder = async () => {
    if (orderItems.length === 0) {
      toast.error('Bitte wählen Sie mindestens ein Produkt');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/tables/${selectedTable.id}/order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: orderItems.map(item => ({
            name: item.name,
            price_net: item.price / (1 + item.tax_rate / 100),
            vat_rate: item.tax_rate,
            quantity: item.quantity || 1
          })),
          course: 1
        })
      });
      
      if (!response.ok) throw new Error('Failed to add order');
      
      const data = await response.json();
      
      toast.success('Bestellung hinzugefügt', {
        description: formatCurrency(data.totals?.total_gross)
      });
      
      setShowOrderDialog(false);
      setOrderItems([]);
      loadTableSession(selectedTable.id);
      loadTables();
    } catch (error) {
      toast.error('Fehler beim Hinzufügen der Bestellung');
    }
  };

  const handleCheckout = async () => {
    const cashAmount = parseFloat(payment.cash) || 0;
    const cardAmount = parseFloat(payment.card) || 0;
    const tipAmount = parseFloat(payment.tip) || 0;
    const totalDue = tableSession?.totals?.total_gross || 0;
    
    if (cashAmount + cardAmount < totalDue) {
      toast.error('Zahlungsbetrag ist zu gering');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/tables/${selectedTable.id}/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cash_amount: cashAmount,
          card_amount: cardAmount,
          tip_amount: tipAmount,
          receipt_type: payment.receiptType || 'standard'
        })
      });
      
      if (!response.ok) throw new Error('Failed to checkout');
      
      const data = await response.json();
      
      const receiptLabel = payment.receiptType === 'hospitality' ? ' (Bewirtungsbeleg)' : '';
      toast.success(`Tisch ${selectedTable.table_number} abgerechnet${receiptLabel}`, {
        description: `${formatCurrency(totalDue)} - Rückgeld: ${formatCurrency(data.change)}`
      });
      
      setShowCheckoutDialog(false);
      setPayment({ cash: '', card: '', tip: '', receiptType: 'standard' });
      setSelectedTable(null);
      setTableSession(null);
      loadTables();
    } catch (error) {
      toast.error('Fehler bei der Abrechnung');
    }
  };

  const handleQuickSetup = async () => {
    try {
      const response = await fetch(`${API_URL}/api/tables/quick-setup?count=${setupCount}`, {
        method: 'POST'
      });
      
      if (!response.ok) throw new Error('Failed to setup tables');
      
      const data = await response.json();
      
      toast.success(`${data.count} Tische erstellt`);
      
      setShowSetupDialog(false);
      loadTables();
    } catch (error) {
      toast.error('Fehler beim Erstellen der Tische');
    }
  };

  const handleSplitTable = async () => {
    if (splitItems.length === 0) {
      toast.error('Bitte wählen Sie mindestens eine Position');
      return;
    }
    if (!splitTargetTable) {
      toast.error('Bitte wählen Sie einen Zieltisch');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/tables/${selectedTable.id}/split`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          item_ids: splitItems,
          new_table_id: splitTargetTable
        })
      });
      
      if (!response.ok) throw new Error('Failed to split table');
      
      const data = await response.json();
      
      toast.success(`${data.moved_items} Positionen verschoben`, {
        description: `Auf Tisch ${tables.find(t => t.id === splitTargetTable)?.table_number || 'Neu'}`
      });
      
      setShowSplitDialog(false);
      setSplitItems([]);
      setSplitTargetTable('');
      loadTables();
      loadTableSession(selectedTable.id);
    } catch (error) {
      toast.error('Fehler beim Splitten des Tisches');
    }
  };

  const handleMergeTables = async () => {
    if (mergeSourceTables.length === 0) {
      toast.error('Bitte wählen Sie mindestens einen Tisch');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/tables/merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source_table_ids: mergeSourceTables,
          target_table_id: selectedTable.id
        })
      });
      
      if (!response.ok) throw new Error('Failed to merge tables');
      
      toast.success('Tische zusammengeführt', {
        description: `Alle Positionen auf Tisch ${selectedTable.table_number}`
      });
      
      setShowMergeDialog(false);
      setMergeSourceTables([]);
      loadTables();
      loadTableSession(selectedTable.id);
    } catch (error) {
      toast.error('Fehler beim Zusammenführen der Tische');
    }
  };

  const toggleSplitItem = (itemId) => {
    if (splitItems.includes(itemId)) {
      setSplitItems(splitItems.filter(id => id !== itemId));
    } else {
      setSplitItems([...splitItems, itemId]);
    }
  };

  const toggleMergeTable = (tableId) => {
    if (mergeSourceTables.includes(tableId)) {
      setMergeSourceTables(mergeSourceTables.filter(id => id !== tableId));
    } else {
      setMergeSourceTables([...mergeSourceTables, tableId]);
    }
  };

  const handleCreateTable = async () => {
    if (!newTable.table_number) {
      toast.error('Bitte Tischnummer eingeben');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/tables/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTable)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create table');
      }
      
      toast.success(`Tisch ${newTable.table_number} erstellt`);
      
      setShowAddDialog(false);
      setNewTable({ table_number: '', capacity: 4, area: '' });
      loadTables();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const addProductToOrder = (product) => {
    const existingIndex = orderItems.findIndex(item => item.product_id === product.product_id);
    
    if (existingIndex >= 0) {
      const updated = [...orderItems];
      updated[existingIndex].quantity = (updated[existingIndex].quantity || 1) + 1;
      setOrderItems(updated);
    } else {
      setOrderItems([...orderItems, { ...product, quantity: 1 }]);
    }
  };

  const removeProductFromOrder = (productId) => {
    setOrderItems(orderItems.filter(item => item.product_id !== productId));
  };

  if (loading) {
    return (
      <div className="p-6">
        <Skeleton className="h-8 w-48 mb-4 bg-slate-700" />
        <div className="grid grid-cols-5 gap-4">
          {[...Array(10)].map((_, i) => (
            <Skeleton key={i} className="h-32 bg-slate-700 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="tischverwaltung-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 
            className="text-2xl font-bold flex items-center gap-3"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            <UtensilsCrossed className="h-7 w-7 text-[#2F80FF]" />
            Tischverwaltung
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            {tables.filter(t => t.status === 'occupied').length} von {tables.length} Tischen besetzt
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            onClick={() => setShowSetupDialog(true)}
            variant="outline"
            className="border-slate-600 hover:bg-slate-700"
            data-testid="quick-setup-btn"
          >
            <Settings className="h-4 w-4 mr-2" />
            Quick-Setup
          </Button>
          <Button
            onClick={() => setShowAddDialog(true)}
            className="bg-[#2F80FF] hover:bg-[#2F80FF]/80"
            data-testid="add-table-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Tisch hinzufügen
          </Button>
          <Button
            onClick={loadTables}
            variant="ghost"
            size="icon"
            className="hover:bg-slate-700"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Tables Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 mb-6">
        {tables.map((table) => {
          const config = statusConfig[table.status] || statusConfig.free;
          const StatusIcon = config.icon;
          const isSelected = selectedTable?.id === table.id;
          
          return (
            <Card
              key={table.id}
              onClick={() => handleTableClick(table)}
              className={`
                p-4 cursor-pointer transition-all duration-200
                ${isSelected ? 'ring-2 ring-[#2F80FF]' : ''}
                hover:scale-105
              `}
              style={{ 
                backgroundColor: table.status === 'occupied' ? '#1a2744' : '#111C32', 
                border: `1px solid ${table.status === 'occupied' ? '#2F80FF50' : '#1E293B'}` 
              }}
              data-testid={`table-card-${table.table_number}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-2xl font-bold">{table.table_number}</span>
                <Badge variant="outline" className={config.color}>
                  <StatusIcon className="h-3 w-3 mr-1" />
                  {config.label}
                </Badge>
              </div>
              
              <div className="flex items-center text-sm text-slate-400 mb-2">
                <Users className="h-4 w-4 mr-1" />
                {table.capacity} Plätze
              </div>
              
              {table.totals && (
                <div className="text-lg font-bold text-emerald-400">
                  {formatCurrency(table.totals.total_gross)}
                </div>
              )}
              
              {table.area && (
                <div className="text-xs text-slate-500 mt-1">{table.area}</div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Selected Table Details */}
      {selectedTable && (
        <Card 
          className="p-6"
          style={{ backgroundColor: '#111C32', border: '1px solid #1E293B' }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Tisch {selectedTable.table_number}</h2>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => { setSelectedTable(null); setTableSession(null); }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {selectedTable.status === 'free' && (
            <div className="text-center py-8">
              <UtensilsCrossed className="h-16 w-16 mx-auto text-slate-600 mb-4" />
              <p className="text-slate-400 mb-4">Tisch ist frei</p>
              <Button
                onClick={() => setShowOpenDialog(true)}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="open-table-btn"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Tisch öffnen
              </Button>
            </div>
          )}
          
          {selectedTable.status === 'occupied' && tableSession && (
            <div>
              <div className="flex items-center gap-4 mb-4 text-sm text-slate-400">
                <span><Users className="h-4 w-4 inline mr-1" /> {tableSession.session?.guest_count || 1} Gäste</span>
                <span><Clock className="h-4 w-4 inline mr-1" /> Geöffnet {new Date(tableSession.session?.opened_at).toLocaleTimeString('de-DE')}</span>
              </div>
              
              {/* Orders */}
              {tableSession.session?.orders?.length > 0 && (
                <div className="mb-4">
                  <h3 className="font-semibold mb-2">Bestellungen</h3>
                  <div className="space-y-2">
                    {tableSession.session.orders.map((order, idx) => (
                      <div key={order.id} className="p-3 rounded bg-[#0E1628]">
                        <div className="text-xs text-slate-500 mb-2">Gang {order.course}</div>
                        {order.items?.map((item, itemIdx) => (
                          <div key={itemIdx} className="flex justify-between text-sm">
                            <span>{item.quantity || 1}x {item.name}</span>
                            <span>{formatCurrency(item.price_net * (item.quantity || 1) * (1 + (item.vat_rate || 19) / 100))}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Totals */}
              {tableSession.totals && (
                <div className="p-4 rounded bg-[#0E1628] mb-4">
                  <div className="flex justify-between text-sm text-slate-400">
                    <span>Netto</span>
                    <span>{formatCurrency(tableSession.totals.total_net)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-slate-400">
                    <span>MwSt</span>
                    <span>{formatCurrency(tableSession.totals.total_vat)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold mt-2 pt-2 border-t border-slate-700">
                    <span>Gesamt</span>
                    <span className="text-emerald-400">{formatCurrency(tableSession.totals.total_gross)}</span>
                  </div>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => setShowOrderDialog(true)}
                  className="flex-1 bg-[#2F80FF] hover:bg-[#2F80FF]/80"
                  data-testid="add-order-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Bestellung
                </Button>
                <Button
                  onClick={() => setShowCheckoutDialog(true)}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  disabled={!tableSession.totals || tableSession.totals.total_gross === 0}
                  data-testid="checkout-btn"
                >
                  <Receipt className="h-4 w-4 mr-2" />
                  Abrechnen
                </Button>
              </div>
              <div className="flex gap-2 mt-2">
                <Button
                  onClick={() => { setSplitItems([]); setSplitTargetTable(''); setShowSplitDialog(true); }}
                  variant="outline"
                  className="flex-1 border-slate-600 hover:bg-slate-700"
                  disabled={!tableSession.session?.orders?.length}
                  data-testid="split-table-btn"
                >
                  <Split className="h-4 w-4 mr-2" />
                  Splitten
                </Button>
                <Button
                  onClick={() => { setMergeSourceTables([]); setShowMergeDialog(true); }}
                  variant="outline"
                  className="flex-1 border-slate-600 hover:bg-slate-700"
                  data-testid="merge-tables-btn"
                >
                  <Merge className="h-4 w-4 mr-2" />
                  Zusammenführen
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Open Table Dialog */}
      <Dialog open={showOpenDialog} onOpenChange={setShowOpenDialog}>
        <DialogContent className="bg-[#111C32] border-slate-700">
          <DialogHeader>
            <DialogTitle>Tisch {selectedTable?.table_number} öffnen</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Anzahl Gäste</Label>
            <div className="flex items-center gap-4 mt-2">
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => setGuestCount(Math.max(1, guestCount - 1))}
                className="border-slate-600"
              >
                -
              </Button>
              <span className="text-3xl font-bold w-12 text-center">{guestCount}</span>
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => setGuestCount(guestCount + 1)}
                className="border-slate-600"
              >
                +
              </Button>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowOpenDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleOpenTable} className="bg-emerald-600 hover:bg-emerald-700">
              <Check className="h-4 w-4 mr-2" />
              Tisch öffnen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order Dialog */}
      <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
        <DialogContent className="bg-[#111C32] border-slate-700 max-w-3xl">
          <DialogHeader>
            <DialogTitle>Bestellung für Tisch {selectedTable?.table_number}</DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            {/* Products */}
            <div className="max-h-96 overflow-y-auto pr-2">
              <h3 className="font-semibold mb-2 text-sm text-slate-400">Produkte</h3>
              <div className="space-y-2">
                {products.slice(0, 20).map((product) => (
                  <div
                    key={product.product_id}
                    onClick={() => addProductToOrder(product)}
                    className="p-2 rounded bg-[#0E1628] cursor-pointer hover:bg-slate-700 flex justify-between"
                  >
                    <span className="text-sm">{product.name}</span>
                    <span className="text-sm text-emerald-400">{formatCurrency(product.price)}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Selected Items */}
            <div>
              <h3 className="font-semibold mb-2 text-sm text-slate-400">Ausgewählte Produkte</h3>
              {orderItems.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Coffee className="h-12 w-12 mx-auto mb-2" />
                  <p className="text-sm">Produkte auswählen</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {orderItems.map((item) => (
                    <div key={item.product_id} className="p-2 rounded bg-[#0E1628] flex justify-between items-center">
                      <div>
                        <span className="text-sm">{item.quantity}x {item.name}</span>
                        <div className="text-xs text-emerald-400">
                          {formatCurrency(item.price * item.quantity)}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeProductFromOrder(item.product_id)}
                        className="h-6 w-6 text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  
                  <div className="pt-2 border-t border-slate-700 flex justify-between font-bold">
                    <span>Gesamt</span>
                    <span className="text-emerald-400">
                      {formatCurrency(orderItems.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0))}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowOrderDialog(false); setOrderItems([]); }}>
              Abbrechen
            </Button>
            <Button onClick={handleAddOrder} className="bg-[#2F80FF] hover:bg-[#2F80FF]/80" disabled={orderItems.length === 0}>
              <Plus className="h-4 w-4 mr-2" />
              Bestellung hinzufügen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Checkout Dialog */}
      <Dialog open={showCheckoutDialog} onOpenChange={setShowCheckoutDialog}>
        <DialogContent className="bg-[#111C32] border-slate-700">
          <DialogHeader>
            <DialogTitle>Tisch {selectedTable?.table_number} abrechnen</DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="p-4 rounded bg-[#0E1628] text-center">
              <div className="text-sm text-slate-400">Zu zahlen</div>
              <div className="text-3xl font-bold text-emerald-400">
                {formatCurrency(tableSession?.totals?.total_gross || 0)}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2">
                  <Banknote className="h-4 w-4 text-yellow-400" />
                  Bargeld
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={payment.cash}
                  onChange={(e) => setPayment({ ...payment, cash: e.target.value })}
                  className="mt-1 bg-[#0E1628] border-slate-700"
                  data-testid="checkout-cash-input"
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-blue-400" />
                  Karte
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={payment.card}
                  onChange={(e) => setPayment({ ...payment, card: e.target.value })}
                  className="mt-1 bg-[#0E1628] border-slate-700"
                  data-testid="checkout-card-input"
                />
              </div>
            </div>
            
            <div>
              <Label className="flex items-center gap-2">
                <Euro className="h-4 w-4 text-purple-400" />
                Trinkgeld (optional)
              </Label>
              <Input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={payment.tip}
                onChange={(e) => setPayment({ ...payment, tip: e.target.value })}
                className="mt-1 bg-[#0E1628] border-slate-700"
                data-testid="checkout-tip-input"
              />
            </div>
            
            {/* Bewirtungsbeleg Option */}
            <div className="flex items-center gap-3 p-3 rounded bg-[#0E1628]">
              <input
                type="checkbox"
                id="hospitality-receipt"
                checked={payment.receiptType === 'hospitality'}
                onChange={(e) => setPayment({ 
                  ...payment, 
                  receiptType: e.target.checked ? 'hospitality' : 'standard' 
                })}
                className="w-5 h-5 rounded border-slate-600 bg-slate-800"
                data-testid="hospitality-checkbox"
              />
              <label htmlFor="hospitality-receipt" className="flex items-center gap-2 cursor-pointer">
                <UtensilsCrossed className="h-4 w-4 text-amber-400" />
                <span className="font-medium">Bewirtungsbeleg</span>
                <span className="text-xs text-slate-500">(für Geschäftsessen)</span>
              </label>
            </div>
            
            {(parseFloat(payment.cash) || 0) + (parseFloat(payment.card) || 0) > (tableSession?.totals?.total_gross || 0) && (
              <div className="p-3 rounded bg-emerald-500/20 text-emerald-400 text-center">
                Rückgeld: {formatCurrency((parseFloat(payment.cash) || 0) + (parseFloat(payment.card) || 0) - (tableSession?.totals?.total_gross || 0))}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCheckoutDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleCheckout} className="bg-emerald-600 hover:bg-emerald-700">
              <Check className="h-4 w-4 mr-2" />
              {payment.receiptType === 'hospitality' ? 'Mit Bewirtungsbeleg' : 'Abrechnen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Table Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-[#111C32] border-slate-700">
          <DialogHeader>
            <DialogTitle>Neuen Tisch hinzufügen</DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label>Tischnummer *</Label>
              <Input
                placeholder="z.B. 1, 2a, Terrasse 1"
                value={newTable.table_number}
                onChange={(e) => setNewTable({ ...newTable, table_number: e.target.value })}
                className="mt-1 bg-[#0E1628] border-slate-700"
              />
            </div>
            <div>
              <Label>Sitzplätze</Label>
              <Input
                type="number"
                min="1"
                max="50"
                value={newTable.capacity}
                onChange={(e) => setNewTable({ ...newTable, capacity: parseInt(e.target.value) || 4 })}
                className="mt-1 bg-[#0E1628] border-slate-700"
              />
            </div>
            <div>
              <Label>Bereich (optional)</Label>
              <Input
                placeholder="z.B. Innen, Terrasse, Bar"
                value={newTable.area}
                onChange={(e) => setNewTable({ ...newTable, area: e.target.value })}
                className="mt-1 bg-[#0E1628] border-slate-700"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleCreateTable} className="bg-[#2F80FF] hover:bg-[#2F80FF]/80">
              <Plus className="h-4 w-4 mr-2" />
              Tisch erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Setup Dialog */}
      <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
        <DialogContent className="bg-[#111C32] border-slate-700">
          <DialogHeader>
            <DialogTitle>Schnell-Setup</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <Label>Anzahl Tische erstellen</Label>
            <Input
              type="number"
              min="1"
              max="50"
              value={setupCount}
              onChange={(e) => setSetupCount(parseInt(e.target.value) || 10)}
              className="mt-2 bg-[#0E1628] border-slate-700"
            />
            <p className="text-xs text-slate-500 mt-2">
              Erstellt Tische mit Nummern 1 bis {setupCount}
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowSetupDialog(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleQuickSetup} className="bg-[#2F80FF] hover:bg-[#2F80FF]/80">
              <Settings className="h-4 w-4 mr-2" />
              Tische erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Split Table Dialog */}
      <Dialog open={showSplitDialog} onOpenChange={setShowSplitDialog}>
        <DialogContent className="bg-[#111C32] border-slate-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Split className="h-5 w-5 text-amber-400" />
              Tisch {selectedTable?.table_number} splitten
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-400">
              Wählen Sie die Positionen, die auf einen anderen Tisch verschoben werden sollen.
            </p>
            
            {/* Items to split */}
            <div className="max-h-60 overflow-y-auto">
              <h4 className="font-semibold mb-2 text-sm">Positionen auswählen</h4>
              {tableSession?.session?.orders?.map((order) => (
                <div key={order.id} className="space-y-1">
                  {order.items?.map((item) => (
                    <div 
                      key={item.item_id}
                      onClick={() => toggleSplitItem(item.item_id)}
                      className={`
                        p-2 rounded cursor-pointer flex justify-between items-center
                        ${splitItems.includes(item.item_id) 
                          ? 'bg-amber-500/20 border border-amber-500/50' 
                          : 'bg-[#0E1628] hover:bg-slate-700'}
                      `}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded border ${
                          splitItems.includes(item.item_id) 
                            ? 'bg-amber-500 border-amber-500' 
                            : 'border-slate-600'
                        }`}>
                          {splitItems.includes(item.item_id) && (
                            <Check className="h-4 w-4 text-white" />
                          )}
                        </div>
                        <span>{item.quantity || 1}x {item.name}</span>
                      </div>
                      <span className="text-emerald-400">
                        {formatCurrency(item.price_net * (item.quantity || 1) * (1 + (item.vat_rate || 19) / 100))}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            
            {/* Target table selection */}
            <div>
              <Label>Zieltisch</Label>
              <Select 
                value={splitTargetTable} 
                onValueChange={setSplitTargetTable}
              >
                <SelectTrigger className="mt-1 bg-[#0E1628] border-slate-700">
                  <SelectValue placeholder="Tisch auswählen..." />
                </SelectTrigger>
                <SelectContent className="bg-[#111C32] border-slate-700">
                  {tables
                    .filter(t => t.id !== selectedTable?.id)
                    .map((table) => (
                      <SelectItem key={table.id} value={table.id}>
                        Tisch {table.table_number} {table.status === 'occupied' ? '(besetzt)' : '(frei)'}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            
            {splitItems.length > 0 && (
              <div className="p-3 rounded bg-amber-500/20 text-amber-400 text-center">
                {splitItems.length} Position(en) ausgewählt
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowSplitDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleSplitTable} 
              className="bg-amber-600 hover:bg-amber-700"
              disabled={splitItems.length === 0 || !splitTargetTable}
              data-testid="confirm-split-btn"
            >
              <Split className="h-4 w-4 mr-2" />
              Positionen verschieben
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Merge Tables Dialog */}
      <Dialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <DialogContent className="bg-[#111C32] border-slate-700 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Merge className="h-5 w-5 text-blue-400" />
              Tische zu Tisch {selectedTable?.table_number} zusammenführen
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-400">
              Wählen Sie die Tische, deren Bestellungen auf Tisch {selectedTable?.table_number} übertragen werden sollen.
            </p>
            
            {/* Source tables */}
            <div className="grid grid-cols-4 gap-2">
              {tables
                .filter(t => t.id !== selectedTable?.id && t.status === 'occupied')
                .map((table) => (
                  <div 
                    key={table.id}
                    onClick={() => toggleMergeTable(table.id)}
                    className={`
                      p-3 rounded cursor-pointer text-center transition-all
                      ${mergeSourceTables.includes(table.id) 
                        ? 'bg-blue-500/20 border-2 border-blue-500' 
                        : 'bg-[#0E1628] border border-slate-700 hover:border-slate-500'}
                    `}
                  >
                    <div className="text-xl font-bold">{table.table_number}</div>
                    <div className="text-xs text-emerald-400">
                      {formatCurrency(table.totals?.total_gross || 0)}
                    </div>
                  </div>
                ))}
            </div>
            
            {tables.filter(t => t.id !== selectedTable?.id && t.status === 'occupied').length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <Users className="h-12 w-12 mx-auto mb-2" />
                <p>Keine anderen besetzten Tische vorhanden</p>
              </div>
            )}
            
            {mergeSourceTables.length > 0 && (
              <div className="p-3 rounded bg-blue-500/20 text-blue-400 text-center">
                {mergeSourceTables.length} Tisch(e) ausgewählt
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowMergeDialog(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleMergeTables} 
              className="bg-blue-600 hover:bg-blue-700"
              disabled={mergeSourceTables.length === 0}
              data-testid="confirm-merge-btn"
            >
              <Merge className="h-4 w-4 mr-2" />
              Tische zusammenführen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
